import { useEffect, useRef, useState } from "react";
import {
  FiChevronDown, FiCpu, FiPlayCircle, FiCreditCard,
  FiLogOut, FiSettings, FiBell, FiHelpCircle, FiUser,
} from "react-icons/fi";
import { useLocation, useNavigate } from "react-router-dom";
import NexaLogo from "./NexaLogo";
import { clearSession } from "../utils/auth";

export default function Header() {
  const [accountOpen, setAccountOpen] = useState(false);
  const [notifOpen,   setNotifOpen]   = useState(false);
  const accountRef = useRef(null);
  const notifRef   = useRef(null);
  const location   = useLocation();
  const navigate   = useNavigate();

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const initials = user?.name?.[0]?.toUpperCase() || "U";

  const navItems = [
    { label: "Chatbots", icon: FiCpu,        paths: ["/chatbots", "/chatbot/create"] },
    { label: "Demo",     icon: FiPlayCircle,  paths: ["/demo"] },
    { label: "Billing",  icon: FiCreditCard,  paths: ["/billing"] },
  ];

  const NOTIFICATIONS = [
    { id: 1, text: "Training completed for Support Bot",  time: "2m ago",  unread: true  },
    { id: 2, text: "New lead captured: Arun Kumar",       time: "18m ago", unread: true  },
    { id: 3, text: "Inbox has 4 open conversations",      time: "1h ago",  unread: false },
    { id: 4, text: "Monthly usage report is ready",       time: "1d ago",  unread: false },
  ];
  const unreadCount = NOTIFICATIONS.filter((n) => n.unread).length;

  useEffect(() => {
    const handler = (e) => {
      if (accountRef.current && !accountRef.current.contains(e.target)) setAccountOpen(false);
      if (notifRef.current   && !notifRef.current.contains(e.target))   setNotifOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleLogout = () => {
    clearSession();
    navigate("/login");
  };

  return (
    <header className="fixed top-0 left-0 right-0 h-16 bg-white border-b shadow-sm z-50 flex items-stretch px-5 gap-2">

      {/* ── Logo ── */}
      <div
        className="flex items-center gap-2.5 cursor-pointer pr-5 shrink-0"
        onClick={() => navigate("/chatbot")}
      >
        <NexaLogo size={32} />
        <div className="flex items-center gap-2">
          <span className="font-bold text-gray-900 text-[15px] tracking-tight">NexaGPT</span>
          <span className="text-[10px] font-semibold bg-primary-light text-primary px-1.5 py-0.5 rounded-full leading-none">
            Pro
          </span>
        </div>
      </div>

      {/* ── Center Nav (full-height tab indicator) ── */}
      <div className="flex items-stretch gap-0.5 flex-1">
        {navItems.map(({ label, icon: Icon, paths }) => {
          const isActive = paths.some((p) => location.pathname.startsWith(p));
          return (
            <button
              key={label}
              onClick={() => navigate(paths[0])}
              className={`relative flex items-center gap-2 px-4 text-sm font-medium transition-colors ${
                isActive ? "text-primary" : "text-gray-500 hover:text-gray-900"
              }`}
            >
              <Icon size={15} />
              {label}
              {isActive && (
                <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-primary rounded-t-full" />
              )}
            </button>
          );
        })}
      </div>

      {/* ── Right side actions ── */}
      <div className="flex items-center gap-1 shrink-0">

        {/* Help */}
        <button
          onClick={() => window.open("https://docs.nexagpt.ai", "_blank")}
          className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition"
          title="Help & Docs"
        >
          <FiHelpCircle size={17} />
        </button>

        {/* Notifications */}
        <div className="relative" ref={notifRef}>
          <button
            onClick={() => { setNotifOpen((v) => !v); setAccountOpen(false); }}
            className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition relative"
            title="Notifications"
          >
            <FiBell size={17} />
            {unreadCount > 0 && (
              <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-red-500 ring-2 ring-white" />
            )}
          </button>

          {notifOpen && (
            <div className="absolute right-0 mt-2 w-80 bg-white border border-gray-200 rounded-2xl shadow-xl overflow-hidden z-50">
              <div className="px-4 py-3 border-b flex items-center justify-between">
                <p className="text-sm font-semibold text-gray-900">Notifications</p>
                {unreadCount > 0 && (
                  <span className="text-[11px] bg-primary-light text-primary font-semibold px-2 py-0.5 rounded-full">
                    {unreadCount} new
                  </span>
                )}
              </div>
              <div className="divide-y max-h-72 overflow-y-auto">
                {NOTIFICATIONS.map((n) => (
                  <div
                    key={n.id}
                    className={`flex items-start gap-3 px-4 py-3 hover:bg-gray-50 transition cursor-pointer ${
                      n.unread ? "bg-primary-light/40" : ""
                    }`}
                  >
                    <div className={`mt-0.5 w-2 h-2 rounded-full shrink-0 ${n.unread ? "bg-primary" : "bg-gray-200"}`} />
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs leading-snug ${n.unread ? "text-gray-900 font-medium" : "text-gray-600"}`}>
                        {n.text}
                      </p>
                      <p className="text-[10px] text-gray-400 mt-0.5">{n.time}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="px-4 py-2.5 border-t text-center">
                <button className="text-xs text-primary font-medium hover:underline">
                  Mark all as read
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="w-px h-6 bg-gray-200 mx-1" />

        {/* Account */}
        <div className="relative" ref={accountRef}>
          <button
            onClick={() => { setAccountOpen((v) => !v); setNotifOpen(false); }}
            className="flex items-center gap-2.5 pl-2 pr-3 py-1.5 rounded-xl hover:bg-gray-100 transition"
          >
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-indigo-400 text-white flex items-center justify-center text-sm font-bold shadow-sm shadow-primary/20">
              {initials}
            </div>
            <div className="text-left hidden sm:block">
              <p className="text-xs font-semibold text-gray-900 leading-tight max-w-[100px] truncate">
                {user?.username || "My Account"}
              </p>
              <p className="text-[10px] text-gray-400 leading-tight max-w-[100px] truncate">
                {user?.email || ""}
              </p>
            </div>
            <FiChevronDown
              size={14}
              className={`text-gray-400 transition-transform shrink-0 ${accountOpen ? "rotate-180" : ""}`}
            />
          </button>

          {accountOpen && (
            <div className="absolute right-0 mt-2 w-52 bg-white border border-gray-200 rounded-2xl shadow-xl overflow-hidden z-50">

              {/* User info header */}
              <div className="px-4 py-3.5 border-b flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-indigo-400 text-white flex items-center justify-center text-sm font-bold shrink-0">
                  {initials}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{user?.username || "User"}</p>
                  <p className="text-xs text-gray-400 truncate">{user?.email || ""}</p>
                  <span className="inline-block mt-1 text-[10px] font-semibold bg-primary-light text-primary px-1.5 py-0.5 rounded-full leading-none">
                    Pro Plan
                  </span>
                </div>
              </div>

              {/* Actions */}
              <div className="py-1">
                <DropdownItem icon={FiUser}       label="Profile"  onClick={() => { navigate("/profile"); setAccountOpen(false); }} />
                <DropdownItem icon={FiSettings}   label="Settings" onClick={() => { navigate("/advanced/settings"); setAccountOpen(false); }} />
                <DropdownItem icon={FiCreditCard} label="Billing"  onClick={() => { navigate("/billing"); setAccountOpen(false); }} />
              </div>

              <div className="border-t py-1">
                <DropdownItem icon={FiLogOut} label="Log out" onClick={handleLogout} danger />
              </div>

            </div>
          )}
        </div>
      </div>
    </header>
  );
}

function DropdownItem({ icon: Icon, label, onClick, danger = false }) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-sm transition ${
        danger ? "text-red-500 hover:bg-red-50" : "text-gray-700 hover:bg-gray-50"
      }`}
    >
      <Icon size={14} className={danger ? "text-red-400" : "text-gray-400"} />
      {label}
    </button>
  );
}
